/**
 * „Jetzt aktualisieren“ in der Karte „Vespera Desktop (Mac)“. (12.10.2026, Bahn server-update-starten)
 *
 * Unter „Bei dir: 1.2.0 · Aktuell: 1.3.0“ steht, nur wenn es etwas zu tun gibt, ein ruhiger Knopf. Ein Tipp startet den Ablauf der
 * Mac-App (POST /api/daemon/update/starten): sie lädt und prüft die neue Fassung und fragt DANN am Mac nach Touch ID. Ohne Finger wird
 * nichts installiert — das sagt der Satz unter dem Knopf in einfachen Worten, in jedem Zustand.
 *
 *   Knopf sichtbar NUR, wenn (updateKnopfMoeglich): der Server das Konto zulässt (`erlaubt`), der Mac verbunden ist, die App
 *   `update_starten` kann UND eine neuere FREIGEGEBENE Fassung bekannt ist (`aktuell` — die Testversion gehört nicht dazu).
 *
 * Der Zustand kommt vom Server als Wort (shared/update-starten.ts) und wird hier in einen Satz übersetzt — nie ein Text vom Mac.
 * Während ein Ablauf läuft, fragt die Seite alle 2 s nach; sonst genügt der 10-s-Abruf der Karte.
 *
 * ## Barrierefreiheit
 *  - Der Satz steht in einem Bereich mit role=status/aria-live=polite, der immer da ist (leer, wenn nichts zu sagen ist) — ein
 *    Vorleseprogramm hört so jeden Wechsel. Der Zustand steht nie nur als Farbe da.
 *  - Der Knopf ist 44 px hoch, bleibt während des Ablaufs fokussierbar (aria-disabled statt disabled: der Fokus geht nicht verloren)
 *    und nennt den Mac per aria-describedby („Jetzt aktualisieren, MacBook Pro“).
 *
 * ⚠️ Nur ein ECHTER Tipp zählt (`isTrusted`): Ein vom Skript ausgelöster Klick (die Chrome-Erweiterung klickt mit `el.click()`)
 *    startet nichts. Das ist ein Zusatz, keine Schranke: ein Klick auf den Bildschirm des Macs (screen_mouse_click) wäre echt. Die
 *    Barriere bleibt Touch ID am Mac — siehe docs/update-starten-2026-10-12.md.
 */
import { istVersion, vergleicheVersion } from "../../shared/app-version.js";
import {
  istUpdateWort,
  updateArt,
  updateErwartetNoch,
  updateSatz,
  UPDATE_KOPF,
  UPDATE_KOPF_WERT,
  UPDATE_PFAD_STARTEN,
  UPDATE_PFAD_ZUSTAND,
  type UpdateArt,
  type UpdateStandAntwort,
  type UpdateWort,
} from "../../shared/update-starten.js";
import type { Geraet } from "./mac-geraete.js";

/** So oft fragt die Seite nach, solange ein Ablauf läuft. */
export const UPDATE_TAKT_MS = 2_000;
/** So lange zeigt eine Zeile ein fertiges Wort noch an (danach ist es Geschichte). */
export const UPDATE_FRISCH_MS = 10 * 60_000;

/**
 * Nach „installiert“ bleibt der Knopf so lange aufgehalten (oder bis der Mac die neue Version meldet — dann verschwindet er ohnehin):
 * Die App startet neu und meldet sich erst nach einer Weile mit der neuen Fassung; bis dahin sähe ein aktiver Knopf aus, als wäre nichts
 * geschehen. (Prüfung Opus, 12.10.2026)
 */
export const UPDATE_NACH_INSTALLIERT_MS = 60_000;

export const KNOPF_TEXT = "Jetzt aktualisieren";

const SATZ_NETZ = "Der Server ist gerade nicht erreichbar. Bitte gleich noch einmal versuchen.";
const SATZ_ANMELDEN = "Bitte melde dich neu an.";
const SATZ_ANDERS = "Das hat nicht geklappt. Bitte lade die Seite neu und versuche es noch einmal.";
const SATZ_UNTERWEGS = "Die Anfrage geht an den Mac …";

/** Soll diese Zeile den Knopf tragen? Eine Stelle für die Regel (der Wächter-Test pinnt jede Bedingung). */
export function updateKnopfMoeglich(
  g: Pick<Geraet, "verbunden" | "version" | "kann_update_starten">,
  s: { aktuell?: string | null; updateStarten?: UpdateStandAntwort | null },
): boolean {
  return (
    s.updateStarten?.erlaubt === true &&
    g.verbunden === true &&
    g.kann_update_starten === true &&
    istVersion(g.version) &&
    istVersion(s.aktuell) &&
    vergleicheVersion(g.version, s.aktuell) < 0
  );
}

/** Eine Antwort des Servers in die Form des Standes bringen — nur Wörter, sonst null. */
export function standAus(roh: unknown): UpdateStandAntwort | null {
  if (!roh || typeof roh !== "object") return null;
  const r = roh as Record<string, unknown>;
  if (typeof r.erlaubt !== "boolean") return null;
  const zustand = r.zustand === null || r.zustand === undefined ? null : istUpdateWort(r.zustand) ? r.zustand : "unbekannt-wort";
  if (zustand === "unbekannt-wort") return null;
  return {
    erlaubt: r.erlaubt,
    zustand,
    fehler: typeof r.fehler === "string" && r.fehler.length <= 40 ? r.fehler : null,
    version: istVersion(r.version) ? r.version : null,
    laeuft: r.laeuft === true,
    um: typeof r.um === "string" && !Number.isNaN(Date.parse(r.um)) ? r.um : null,
    geraet: typeof r.geraet === "string" && r.geraet.length <= 80 ? r.geraet : null,
  };
}

/** Der Satz und die Art für eine Zeile — oder null, wenn dort nichts zu sagen ist. */
export interface UpdateAnzeige {
  text: string;
  art: UpdateArt;
}

/** Ein Hinweis, der nicht vom Ablauf kommt (Anfrage unterwegs, Server nicht erreichbar, Start abgewiesen). */
export interface LokalerHinweis {
  geraet: string;
  text: string;
  art: UpdateArt;
  um: number;
}

const MIT_VERSION: ReadonlySet<UpdateWort> = new Set<UpdateWort>(["geladen", "wartet-auf-touchid", "installiert"]);

/** Was die Zeile dieses Macs sagt: der laufende Ablauf zuerst, dann der neuere von Hinweis und fertigem Wort. */
export function updateAnzeige(geraetId: string, stand: UpdateStandAntwort | null, lokal: LokalerHinweis | null, jetzt: number): UpdateAnzeige | null {
  const eigener = stand?.geraet === geraetId ? stand : null;
  const wort = eigener?.zustand ?? null;
  const umStand = eigener?.um ? Date.parse(eigener.um) : 0;
  const hinweis = lokal?.geraet === geraetId ? lokal : null;
  if (eigener && wort && eigener.laeuft) return vomStand(eigener, wort);
  if (hinweis && hinweis.um >= umStand) return { text: hinweis.text, art: hinweis.art };
  if (eigener && wort && umStand > 0 && jetzt - umStand < UPDATE_FRISCH_MS) return vomStand(eigener, wort);
  return null;
}

function vomStand(stand: UpdateStandAntwort, wort: UpdateWort): UpdateAnzeige {
  const version = stand.version && MIT_VERSION.has(wort) ? ` Neue Version: ${stand.version}.` : "";
  return { text: `${updateSatz(wort, stand.fehler)}${version}`, art: updateArt(wort) };
}

// ---------------------------------------------------------------------------
// Die zwei Aufrufe
// ---------------------------------------------------------------------------

export interface StartAntwort {
  /** HTTP-Status — null, wenn keine Antwort kam. */
  status: number | null;
  daten: Record<string, unknown> | null;
}

/** POST /api/daemon/update/starten — mit dem Sonderkopf der eigenen Seite (CSRF, server/update-starten-route.ts). */
export async function starteUpdate(geraet: string): Promise<StartAntwort> {
  try {
    const res = await fetch(UPDATE_PFAD_STARTEN, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", [UPDATE_KOPF]: UPDATE_KOPF_WERT },
      body: JSON.stringify({ geraet }),
    });
    const daten = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    return { status: res.status, daten };
  } catch {
    return { status: null, daten: null };
  }
}

/** GET /api/daemon/update/zustand — der Stand des Ablaufs, oder null. */
export async function holeUpdateStand(): Promise<UpdateStandAntwort | null> {
  try {
    const res = await fetch(UPDATE_PFAD_ZUSTAND, { credentials: "same-origin", cache: "no-store" });
    if (!res.ok) return null;
    return standAus(await res.json().catch(() => null));
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Die Steuerung für die Liste
// ---------------------------------------------------------------------------

export interface UpdateSteuerungOptionen {
  /** Die Liste, in der die Zeilen stehen (solange sie nicht im Dokument hängt, fragt die Steuerung nicht mehr nach). */
  anker: HTMLElement;
  /** Ein Ablauf ist zu Ende: die Karte holt ihren ganzen Stand neu (neue Version?). */
  nachher: () => void;
  /** Eine Zeile braucht einen Block, hat aber keinen (der Stand kam nach dem Zeichnen, z. B. nach dem Neuladen der Seite). */
  neuZeichnen: () => void;
  /** Die Uhr (Tests). */
  jetzt?: () => number;
}

export interface UpdateSteuerung {
  /** Die Teile für eine Zeile: Knopf (wenn möglich) und der Bereich für den Satz, der immer da ist. */
  teile(g: Geraet, s: { aktuell?: string | null; updateStarten?: UpdateStandAntwort | null }, nameId: string, satzId: string): HTMLElement[];
  /** Der Stand vom regelmäßigen Abruf der Karte (`updateStarten` in /api/daemon-status). */
  stand(u: UpdateStandAntwort | null | undefined): void;
}

interface Teil {
  satz: HTMLElement;
  knopf: HTMLButtonElement | null;
}

export function baueUpdateSteuerung(o: UpdateSteuerungOptionen): UpdateSteuerung {
  const jetzt = o.jetzt ?? Date.now;
  let aktuell: UpdateStandAntwort | null = null;
  let lokal: LokalerHinweis | null = null;
  let anfrage = false;
  let takt: number | null = null;
  let neuGezeichnetFuer = "";
  /** Wann (nach UNSERER Uhr) die Seite „installiert“ zum ersten Mal sah, und von welchem Stand (`um`) — nie die Uhr des Servers allein. */
  let installiertGesehen = 0;
  let installiertMarke = "";
  let entsperrUhr: number | null = null;
  const teileJeMac = new Map<string, Teil>();

  /** „Installiert“ ist erst kurz her (beide Uhren: die des Servers und die eigene) — der Mac startet gerade neu. */
  const installiertFrisch = (id: string): boolean =>
    aktuell?.zustand === "installiert" &&
    aktuell.geraet === id &&
    jetzt() - installiertGesehen < UPDATE_NACH_INSTALLIERT_MS &&
    jetzt() - Date.parse(aktuell.um ?? "") < UPDATE_NACH_INSTALLIERT_MS;

  const laeuftAuf = (id: string): boolean => anfrage || (!!aktuell?.laeuft && aktuell.geraet === id) || installiertFrisch(id);

  /** Alle gezeichneten Zeilen auf den neuen Stand bringen — an Ort und Stelle, ohne Neuaufbau (der Fokus bleibt). */
  const zeichneAlle = (): void => {
    for (const [id, t] of [...teileJeMac]) {
      if (!t.satz.isConnected) {
        teileJeMac.delete(id);
        continue;
      }
      const a = updateAnzeige(id, aktuell, lokal, jetzt());
      const text = a?.text ?? "";
      if (t.satz.textContent !== text) t.satz.textContent = text;
      if (a) t.satz.dataset.art = a.art;
      else delete t.satz.dataset.art;
      if (t.knopf) {
        const aktiv = laeuftAuf(id);
        if (aktiv) {
          t.knopf.setAttribute("aria-disabled", "true");
          t.knopf.setAttribute("aria-busy", "true");
        } else {
          t.knopf.removeAttribute("aria-disabled");
          t.knopf.removeAttribute("aria-busy");
        }
      }
    }
  };

  const taktStoppen = (): void => {
    if (takt !== null) window.clearInterval(takt);
    takt = null;
  };
  const taktStarten = (): void => {
    if (takt !== null) return;
    takt = window.setInterval(() => {
      if (!o.anker.isConnected) {
        taktStoppen();
        return;
      }
      void holeUpdateStand().then((neu) => {
        if (neu) uebernehmen(neu);
      });
    }, UPDATE_TAKT_MS);
  };

  /** Einen Stand annehmen — nie einen älteren als den, den die Seite schon hat. */
  const uebernehmen = (neu: UpdateStandAntwort): void => {
    const vorherLief = !!aktuell?.laeuft;
    const alt = aktuell?.um ? Date.parse(aktuell.um) : 0;
    const um = neu.um ? Date.parse(neu.um) : 0;
    if (aktuell && um < alt) return;
    aktuell = neu;
    // Ein Hinweis, der älter ist als das neue Wort, gilt nicht mehr (zeichneAlle wählt nach der Zeit).
    // Weiter nachfragen, solange der Server noch auf den Mac wartet — auch nach einem Abriss der Leitung: der Mac fragt womöglich
    // weiter nach Touch ID und der Server holt sein späteres Wort nach (bis zu seiner Frist, dann `keine-antwort`).
    if (updateErwartetNoch(neu.zustand)) taktStarten();
    else taktStoppen();
    // „Installiert“: den Knopf eine Weile aufhalten, dann wieder freigeben (oder er verschwindet mit der neuen Version).
    const marke = `${neu.geraet}|${neu.um}`;
    if (neu.zustand === "installiert" && marke !== installiertMarke) {
      installiertMarke = marke;
      installiertGesehen = jetzt();
      if (entsperrUhr !== null) window.clearTimeout(entsperrUhr);
      entsperrUhr = window.setTimeout(() => {
        entsperrUhr = null;
        zeichneAlle();
      }, UPDATE_NACH_INSTALLIERT_MS + 50);
    }
    // Eine Zeile, die etwas zu sagen hat, aber keinen Bereich dafür: einmal je Wort neu zeichnen (nicht bei jedem Abruf).
    const zeichenMarke = `${neu.geraet}|${neu.um}`;
    if (neu.geraet && updateAnzeige(neu.geraet, neu, null, jetzt()) && !teileJeMac.has(neu.geraet) && zeichenMarke !== neuGezeichnetFuer) {
      neuGezeichnetFuer = zeichenMarke;
      o.neuZeichnen();
    }
    zeichneAlle();
    if (vorherLief && !neu.laeuft) o.nachher();
  };

  const abgewiesen = (status: number | null, daten: Record<string, unknown> | null): { text: string; art: UpdateArt } => {
    if (status === null) return { text: SATZ_NETZ, art: "fehler" };
    if (status === 401) return { text: SATZ_ANMELDEN, art: "fehler" };
    const wort = daten?.error;
    if (istUpdateWort(wort)) return { text: updateSatz(wort), art: updateArt(wort) };
    return { text: SATZ_ANDERS, art: "fehler" };
  };

  const tippen = async (g: Geraet): Promise<void> => {
    if (laeuftAuf(g.id)) return;
    anfrage = true;
    lokal = { geraet: g.id, text: SATZ_UNTERWEGS, art: "laeuft", um: jetzt() };
    zeichneAlle();
    const r = await starteUpdate(g.id);
    anfrage = false;
    const stand = standAus(r.daten);
    if (r.status === 200 && r.daten?.ok === true && stand) {
      lokal = null;
      uebernehmen(stand);
      return;
    }
    const a = abgewiesen(r.status, r.daten);
    lokal = { geraet: g.id, text: a.text, art: a.art, um: jetzt() };
    // Auch eine Abweisung trägt den Stand (zum Beispiel „es läuft schon“): dann gilt der laufende Ablauf.
    if (stand) uebernehmen(stand);
    else zeichneAlle();
  };

  return {
    teile(g, s, nameId, satzId) {
      const teile: HTMLElement[] = [];
      let knopf: HTMLButtonElement | null = null;
      if (updateKnopfMoeglich(g, s)) {
        const box = document.createElement("div");
        box.className = "mac-geraet-aktionen mac-update-aktion";
        knopf = document.createElement("button");
        knopf.type = "button";
        knopf.className = "mac-knopf";
        knopf.dataset.aktion = "aktualisieren";
        knopf.textContent = KNOPF_TEXT;
        knopf.setAttribute("aria-describedby", `${nameId} ${satzId}`);
        // Nur ein echter Tipp zählt (siehe Kopf). Ein laufender Ablauf nimmt keinen zweiten an (aria-disabled lässt den Fokus stehen).
        knopf.addEventListener("click", (ev) => {
          if (!ev.isTrusted) return;
          void tippen(g);
        });
        box.appendChild(knopf);
        teile.push(box);
      }
      const satz = document.createElement("p");
      satz.className = "mac-update-satz";
      satz.id = satzId;
      satz.setAttribute("role", "status");
      satz.setAttribute("aria-live", "polite");
      satz.setAttribute("aria-atomic", "true");
      teile.push(satz);
      teileJeMac.set(g.id, { satz, knopf });
      // Sofort den aktuellen Satz setzen (Seite neu geladen, Zeile neu gebaut): das ist keine Änderung vor den Ohren, nur Stand.
      const a = updateAnzeige(g.id, aktuell, lokal, jetzt());
      if (a) {
        satz.textContent = a.text;
        satz.dataset.art = a.art;
      }
      if (knopf && laeuftAuf(g.id)) {
        knopf.setAttribute("aria-disabled", "true");
        knopf.setAttribute("aria-busy", "true");
      }
      return teile;
    },
    stand(u) {
      const neu = u ? standAus(u) : null;
      if (neu) uebernehmen(neu);
    },
  };
}
